import { Controller } from "@hotwired/stimulus"

// Library picker for the Feature show page's Media tab.
// Debounced search → paginated grid. Clicking a tile duplicates that
// MediaItem's blob onto this feature (no replace_existing — the feature
// can have many media items side by side). Page reloads on success so
// the newly attached tile shows up in the media list above.
export default class extends Controller {
  static targets = [
    "search", "grid", "pagination", "prevBtn", "nextBtn", "counter", "error"
  ]

  static values = {
    featureId: String
  }

  connect() {
    this._page = 1
    this._pages = 1
    this._query = ""
    this._timer = null
  }

  searchChanged() {
    clearTimeout(this._timer)
    this._timer = setTimeout(() => {
      this._query = this.searchTarget.value.trim()
      this._page = 1
      if (this._query.length === 0) {
        this._renderHint()
        return
      }
      this._fetch()
    }, 250)
  }

  prev() {
    if (this._page <= 1) return
    this._page -= 1
    this._fetch()
  }

  next() {
    if (this._page >= this._pages) return
    this._page += 1
    this._fetch()
  }

  pick(event) {
    const button = event.currentTarget
    const sourceId = button.dataset.mediaId
    if (!sourceId || !this.featureIdValue) return

    button.disabled = true

    const fd = new FormData()
    fd.append("duplicate_from_id", sourceId)
    fd.append("mediable_type", "Feature")
    fd.append("mediable_id", this.featureIdValue)

    fetch("/admin/media_items.json", {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this._csrfToken(),
        "X-Requested-With": "XMLHttpRequest"
      },
      body: fd
    })
      .then(r => r.json().then(data => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          this._showError((data.errors || ["Falha ao adicionar."]).join(", "))
          button.disabled = false
          return
        }
        // Reload so the media list above re-renders with the new item.
        window.location.reload()
      })
      .catch(() => {
        this._showError("Falha ao adicionar.")
        button.disabled = false
      })
  }

  _fetch() {
    this.gridTarget.innerHTML =
      "<div class='col-span-6 text-xs text-base-content/40 py-4 text-center'>Carregando…</div>"
    this._clearError()

    const params = new URLSearchParams()
    params.set("page", this._page)
    params.set("per_page", 18)
    if (this._query) params.set("q", this._query)

    fetch(`/admin/media_items.json?${params}`, {
      headers: {
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(payload => {
        const items = payload.items || []
        const pagy  = payload.pagy  || { page: 1, pages: 1, count: items.length }
        this._pages = pagy.pages || 1
        this._render(items)
        this._updatePagination(pagy)
      })
      .catch(() => {
        this.gridTarget.innerHTML =
          "<div class='col-span-6 text-xs text-error py-4 text-center'>Falha ao carregar biblioteca.</div>"
      })
  }

  _render(items) {
    if (!items.length) {
      this.gridTarget.innerHTML =
        "<div class='col-span-6 text-xs text-base-content/40 py-4 text-center'>Nada encontrado.</div>"
      return
    }
    this.gridTarget.innerHTML = ""
    items.forEach(mi => {
      const tile = document.createElement("button")
      tile.type = "button"
      tile.className = "relative aspect-video rounded overflow-hidden bg-base-200 group cursor-pointer ring-0 hover:ring-2 hover:ring-primary transition"
      tile.setAttribute("data-action", "click->feature-library-picker#pick")
      tile.setAttribute("data-media-id", mi.id)
      tile.innerHTML = this._tileInner(mi) +
        `<span class="absolute top-1 right-1 bg-primary text-primary-content text-[10px] rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition">+</span>`
      this.gridTarget.appendChild(tile)
    })
  }

  _tileInner(mi) {
    const src = mi.variant_url || mi.thumbnail_url || mi.url || ""
    if (mi.media_type === "video") {
      if (src) {
        return `
          <img src="${this._escape(src)}" class="w-full h-full object-cover" loading="lazy" alt="" />
          <div class="absolute inset-0 bg-black/30 flex items-center justify-center pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-white/90" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd" />
            </svg>
          </div>`
      }
      return `<div class="w-full h-full flex items-center justify-center text-xs text-base-content/50">video</div>`
    }
    if (src) {
      return `<img src="${this._escape(src)}" class="w-full h-full object-cover" loading="lazy" alt="" />`
    }
    return `<div class="w-full h-full flex items-center justify-center text-xs text-base-content/50">${this._escape(mi.media_type || "")}</div>`
  }

  _updatePagination(pagy) {
    this.paginationTarget.hidden = (pagy.pages || 1) <= 1
    this.counterTarget.textContent = `Página ${pagy.page || 1} de ${pagy.pages || 1} · ${pagy.count || 0}`
    this.prevBtnTarget.disabled = (pagy.page || 1) <= 1
    this.nextBtnTarget.disabled = (pagy.page || 1) >= (pagy.pages || 1)
  }

  _renderHint() {
    this.gridTarget.innerHTML =
      "<div class='col-span-6 text-xs text-base-content/40 py-4 text-center'>Digite para buscar itens da biblioteca.</div>"
    this.paginationTarget.hidden = true
  }

  _showError(msg) {
    this.errorTarget.textContent = msg
    this.errorTarget.classList.remove("hidden")
  }

  _clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  _csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  _escape(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
